/**
 * Navigation behaviour: the language selector and the mobile menu overlay.
 *
 * Requires the `strichwerke` namespace from global.js.
 */

// Language Selector — works with Shopify's {% form 'localization' %}
strichwerke.LanguageSelector = {
  init: function () {
    this.setupLanguageToggle();
    this.setupLanguageOptions();
    this.updateLanguageSelectorFromUrl();
    this.appendLocaleToLinksForPreview();
    this.updateVisibleContent(this.getCurrentLocale());
    this.autoDetectLanguage();
  },

  getCurrentLocale: function () {
    const urlParams = new URLSearchParams(window.location.search);
    const previewLocale = urlParams.get("locale");
    if (previewLocale) return previewLocale;

    const docLang = (
      document.documentElement.getAttribute("lang") || ""
    ).toLowerCase();
    if (docLang.startsWith("de")) return "de";
    return "en";
  },

  updateVisibleContent: function (locale) {
    const activeLocale = locale === "de" ? "de" : "en";
    document.querySelectorAll("[data-lang]").forEach((el) => {
      if (el.getAttribute("data-lang") === activeLocale) {
        el.style.display = "";
      } else {
        el.style.display = "none";
      }
    });
  },

  clickOutsideHandler: null,

  appendLocaleToLinksForPreview: function () {
    const urlParams = new URLSearchParams(window.location.search);
    const currentLocale = urlParams.get("locale");
    if (currentLocale) {
      document.querySelectorAll("a").forEach((link) => {
        try {
          if (
            link.hostname === window.location.hostname &&
            link.pathname &&
            !link.href.includes("javascript:")
          ) {
            const url = new URL(link.href);
            if (!url.searchParams.has("locale")) {
              url.searchParams.set("locale", currentLocale);
              link.href = url.toString();
            }
          }
        } catch (e) {}
      });
    }
  },

  updateLanguageSelectorFromUrl: function () {
    const urlParams = new URLSearchParams(window.location.search);
    const currentLocale = urlParams.get("locale");
    if (currentLocale) {
      const toggleText = document.querySelector(".language-current");
      const activeOption = document.querySelector(
        `.language-option[data-value="${currentLocale}"]`,
      );

      if (activeOption && toggleText) {
        const optionText =
          activeOption.querySelector("span:not(.flag)").textContent;
        toggleText.textContent = optionText;

        document.querySelectorAll(".language-option").forEach((opt) => {
          opt.classList.remove("active");
        });
        activeOption.classList.add("active");
      }
    }
  },

  setupLanguageToggle: function () {
    const toggle = document.querySelector(".language-toggle");
    if (!toggle) return;

    toggle.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const isExpanded = toggle.getAttribute("aria-expanded") === "true";
      this.setDropdownState(!isExpanded);
    });
  },

  setupLanguageOptions: function () {
    const options = document.querySelectorAll(".language-option");
    options.forEach((option) => {
      option.addEventListener("click", (e) => {
        e.preventDefault();
        const value = option.dataset.value || option.value;
        if (value) {
          try {
            window.localStorage.setItem("strichwerkeLocaleChoice", value);
          } catch (error) {}
        }
        const form = option.closest("form");
        const input = form?.querySelector('input[name="locale"]');
        const localeUrl = option.dataset.url?.replace(/([^:]\/)\/+/g, "$1");

        if (input && value) {
          const isUnpublished = !localeUrl;

          // Use actual locale routes when available, otherwise fall back to preview query params.
          if (!isUnpublished) {
            if (input.value === value) {
              this.setDropdownState(false);
              return;
            }

            const searchParams = new URLSearchParams(window.location.search);
            searchParams.delete("locale");
            const searchString = searchParams.toString();
            const targetUrl =
              localeUrl + (searchString ? `?${searchString}` : "");

            window.location.href = targetUrl;
            return;
          }

          const currentUrl = new URL(window.location.href);
          const currentLocale =
            currentUrl.searchParams.get("locale") || input.value;

          if (currentLocale === value) {
            this.setDropdownState(false);
            return;
          }

          if (value === input.value) {
            currentUrl.searchParams.delete("locale");
          } else {
            currentUrl.searchParams.set("locale", value);
          }

          window.location.href = currentUrl.pathname + currentUrl.search;
          return;
        }
      });
    });
  },

  setDropdownState: function (isOpen) {
    const toggle = document.querySelector(".language-toggle");
    const dropdown = document.querySelector(".language-dropdown");

    if (!toggle || !dropdown) return;

    if (isOpen) {
      toggle.setAttribute("aria-expanded", "true");
      dropdown.removeAttribute("hidden");
      // Close dropdown when clicking outside
      if (!this.clickOutsideHandler) {
        this.clickOutsideHandler = this.handleClickOutside.bind(this);
      }
      document.addEventListener("click", this.clickOutsideHandler);
    } else {
      toggle.setAttribute("aria-expanded", "false");
      dropdown.setAttribute("hidden", "");
      if (this.clickOutsideHandler) {
        document.removeEventListener("click", this.clickOutsideHandler);
      }
    }
  },

  handleClickOutside: function (e) {
    const selector = document.querySelector(".language-selector");
    if (!selector?.contains(e.target)) {
      this.setDropdownState(false);
    }
  },

  autoDetectLanguage: function () {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("locale") || window.location.pathname !== "/") return;

    try {
      if (window.localStorage.getItem("strichwerkeLocaleChoice")) return;
    } catch (error) {}

    const browserLang = (navigator.language || "").toLowerCase();
    if (browserLang.startsWith("de")) {
      const targetUrl = new URL(window.location.href);
      targetUrl.pathname = "/de/";
      window.location.replace(targetUrl.toString());
    }
  },

};

strichwerke.MobileMenu = {
  init: function () {
    this.toggleButton = document.querySelector(".mobile-menu-toggle");
    this.overlay = document.querySelector(".mobile-menu-overlay");
    this.closeButton = document.querySelector(".mobile-menu-close");
    this.languageToggles = document.querySelectorAll(
      ".mobile-menu-language-toggle",
    );

    if (!this.toggleButton || !this.overlay) return;

    this.setupEventListeners();
  },

  setupEventListeners: function () {
    // Toggle menu on button click
    this.toggleButton.addEventListener("click", () => {
      this.toggleMenu();
    });

    // Close menu on close button click
    if (this.closeButton) {
      this.closeButton.addEventListener("click", () => {
        this.closeMenu();
      });
    }

    // Close menu on overlay click
    this.overlay.addEventListener("click", (e) => {
      if (e.target === this.overlay) {
        this.closeMenu();
      }
    });

    // Close menu on escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.overlay && !this.overlay.hidden) {
        this.closeMenu();
      }
    });

    // Setup language dropdown toggles
    this.languageToggles.forEach((toggle) => {
      toggle.addEventListener("click", (e) => {
        e.stopPropagation();
        const dropdown = toggle.nextElementSibling;
        if (
          dropdown &&
          dropdown.classList.contains("mobile-menu-language-dropdown")
        ) {
          const isExpanded = toggle.getAttribute("aria-expanded") === "true";
          toggle.setAttribute("aria-expanded", !isExpanded);
          dropdown.hidden = isExpanded;
        }
      });
    });

    // Close language dropdowns when clicking outside
    document.addEventListener("click", (e) => {
      this.languageToggles.forEach((toggle) => {
        const dropdown = toggle.nextElementSibling;
        if (
          dropdown &&
          dropdown.classList.contains("mobile-menu-language-dropdown")
        ) {
          if (!dropdown.contains(e.target) && e.target !== toggle) {
            toggle.setAttribute("aria-expanded", "false");
            dropdown.hidden = true;
          }
        }
      });
    });
  },

  toggleMenu: function () {
    const isExpanded =
      this.toggleButton.getAttribute("aria-expanded") === "true";
    this.toggleButton.setAttribute("aria-expanded", !isExpanded);
    this.overlay.classList.toggle("is-open", !isExpanded);

    // Prevent body scroll when menu is open
    if (!isExpanded) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  },

  closeMenu: function () {
    this.toggleButton.setAttribute("aria-expanded", "false");
    this.overlay.classList.remove("is-open");
    document.body.style.overflow = "";
  },
};
